import { useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PROMPT_SCOPE_LABEL, type PromptScopeType } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Alert, Bolt, Check, Copy } from "../../components/icons.js";
import { apiErrorText } from "./adminUi.js";
import { useAdminVerticals } from "./Verticals.js";

/**
 * Admin · AI Prompts — the six core system prompts behind interviewing and scoring, editable per
 * SCOPE (migration 0055): platform, one vertical, one organisation, or one organisation on one
 * vertical. The most specific override wins:
 *
 *     org + vertical  >  org  >  vertical  >  platform  >  the default that ships in code
 *
 * Two things this page has to be honest about, because getting either wrong leaves an owner
 * confident about a prompt that is not the one running:
 *
 *   1. WHAT IS SET HERE vs WHAT RUNS HERE. A scope with no override of its own still runs
 *      something — whatever it inherits. The card shows the running value and names where it came
 *      from. "Reset to default" clears the override at THIS scope only; the prompt then falls back
 *      to whatever the wider scope provides, which is not necessarily the code default.
 *   2. WHAT CANNOT BE EDITED. An override replaces the body wholesale, so the platform rules
 *      (protected attributes, never-auto-reject, the judge's evidence and fluency rules, the JSON
 *      contract) are appended by the API afterwards. They are rendered read-only rather than
 *      hidden — an owner who cannot see them writes a prompt that fights them.
 */

type ResolvedSource = PromptScopeType | "default";

interface ScopeSummary {
  scopeType: PromptScopeType;
  orgId: string | null;
  verticalKey: string | null;
  length: number;
}

interface PromptRow {
  key: string;
  label: string;
  appliesTo: string;
  defaultText: string;
  lockedSuffix: string;
  /** The body stored at exactly the selected scope, or null when nothing is set here. */
  scopeOverride: string | null;
  /** 0100: the override here was written for the pre-0099 scoring contract — stored but NOT applied. */
  scopeOverrideLegacy?: boolean;
  resolved: {
    body: string;
    /** Body + platform rules, exactly as the model receives it. */
    text: string;
    source: ResolvedSource;
    sourceOrgId: string | null;
    sourceVerticalKey: string | null;
  };
  scopes: ScopeSummary[];
}

interface PromptsResponse {
  scope: { orgId: string | null; verticalKey: string | null };
  prompts: PromptRow[];
}

interface OrgSummary {
  id: string;
  name: string;
}
interface OrgsResponse {
  orgs: OrgSummary[];
}

const SCOPE_MODES: { mode: PromptScopeType; label: string }[] = [
  { mode: "platform", label: "Platform" },
  { mode: "vertical", label: "By vertical" },
  { mode: "org", label: "By organisation" },
  { mode: "org_vertical", label: "Org + vertical" },
];

const MONO: CSSProperties = {
  whiteSpace: "pre-wrap",
  fontFamily: "var(--mono)",
  fontSize: 12,
  lineHeight: 1.6,
  background: "var(--surface-2)",
  borderRadius: 10,
  padding: "12px 14px",
  margin: 0,
  maxHeight: 200,
  overflowY: "auto",
};

/** Where the value that actually runs came from, in words an owner can act on. */
function sourceLabel(
  p: PromptRow,
  orgName: (id: string | null) => string,
  verticalName: (key: string | null) => string,
): string {
  const r = p.resolved;
  switch (r.source) {
    case "default":
      return "the built-in default";
    case "platform":
      return "the platform override";
    case "vertical":
      return `the ${verticalName(r.sourceVerticalKey)} vertical override`;
    case "org":
      return `${orgName(r.sourceOrgId)}'s override`;
    case "org_vertical":
      return `the ${orgName(r.sourceOrgId)} · ${verticalName(r.sourceVerticalKey)} override`;
  }
}

function PromptCard({
  p,
  isOwner,
  scope,
  scopeName,
  orgName,
  verticalName,
}: {
  p: PromptRow;
  isOwner: boolean;
  scope: { orgId: string | null; verticalKey: string | null };
  /** Human name of the scope being edited — used in every button and every warning. */
  scopeName: string;
  orgName: (id: string | null) => string;
  verticalName: (key: string | null) => string;
}): ReactNode {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [showLocked, setShowLocked] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  const save = useMutation({
    mutationFn: (override: string | null) =>
      api.put(`/admin/model-prompts/${p.key}`, { override, ...scope }),
    onSuccess: () => {
      setEditing(false);
      setErr(null);
      void qc.invalidateQueries({ queryKey: ["model-prompts"] });
    },
    onError: (e) => setErr(apiErrorText(e, "Couldn't save the prompt.")),
  });

  const setHere = p.scopeOverride !== null;
  // Editing starts from what RUNS here, not from the code default: narrowing a platform override
  // to one org almost always means "like that, but…", and opening on a blank-slate default would
  // silently discard the wording being refined.
  const startingDraft = p.scopeOverride ?? p.resolved.body;
  const elsewhere = p.scopes.filter(
    (s) => !(s.orgId === scope.orgId && s.verticalKey === scope.verticalKey),
  );

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <div>
          <h3>{p.label}</h3>
          <div className="mut sm" style={{ marginTop: 3, maxWidth: 720 }}>
            {p.appliesTo}
          </div>
        </div>
        <span className={setHere ? "pill t" : "pill n"}>{setHere ? "set here" : "inherited"}</span>
        {p.scopeOverrideLegacy && (
          <span
            className="pill r"
            title="Written for the old per-question scoring contract. It is kept but NOT applied — the platform default (or a broader override) runs instead. Review it against the evidence-based contract and save it again to activate it."
          >
            inactive — old scoring contract
          </span>
        )}
      </div>
      <div className="card-b">
        {!editing && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 6,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div className="mut sm">
                Running for <b>{scopeName}</b> — from {sourceLabel(p, orgName, verticalName)}.
              </div>
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => {
                  void navigator.clipboard.writeText(p.resolved.body).catch(() => undefined);
                  setCopiedPrompt(true);
                  setTimeout(() => setCopiedPrompt(false), 2000);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 8px",
                  fontSize: 12,
                }}
                title="Copy prompt body"
              >
                {copiedPrompt ? (
                  <>
                    <Check style={{ width: 12, height: 12, color: "var(--good)" }} />
                    <span style={{ color: "var(--good)", fontWeight: 600 }}>Copied</span>
                  </>
                ) : (
                  <>
                    <Copy style={{ width: 12, height: 12 }} />
                    <span>Copy prompt</span>
                  </>
                )}
              </button>
            </div>
            <pre style={MONO}>{p.resolved.body}</pre>

            <button
              className="btn ghost sm"
              style={{ marginTop: 10 }}
              onClick={() => setShowLocked((v) => !v)}
            >
              {showLocked ? "Hide" : "Show"} the rules that are always added
            </button>
            {showLocked && (
              <>
                <div className="mut sm" style={{ margin: "10px 0 6px" }}>
                  Appended by the platform after whatever wording is above, on every run. No
                  override can remove these.
                </div>
                <pre style={{ ...MONO, opacity: 0.85 }}>{p.lockedSuffix}</pre>
              </>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <button
                className="btn sm"
                disabled={!isOwner}
                onClick={() => {
                  setDraft(startingDraft);
                  setEditing(true);
                }}
              >
                <Bolt /> {setHere ? "Edit this override" : "Override for this scope"}
              </button>
              {setHere && (
                <button
                  className="btn ghost sm"
                  disabled={!isOwner || save.isPending}
                  onClick={() => save.mutate(null)}
                >
                  Reset to default
                </button>
              )}
            </div>
            {setHere && (
              <p className="mut sm" style={{ margin: "8px 0 0" }}>
                Reset clears the override for {scopeName}; this prompt then falls back to whatever
                the wider scope provides.
              </p>
            )}
            {elsewhere.length > 0 && (
              <p className="mut sm" style={{ margin: "8px 0 0" }}>
                Also overridden at:{" "}
                {elsewhere
                  .map((s) =>
                    s.scopeType === "platform"
                      ? "Platform"
                      : s.scopeType === "vertical"
                        ? verticalName(s.verticalKey)
                        : s.scopeType === "org"
                          ? orgName(s.orgId)
                          : `${orgName(s.orgId)} · ${verticalName(s.verticalKey)}`,
                  )
                  .join(", ")}
                .
              </p>
            )}
            {!isOwner && (
              <p className="mut sm" style={{ margin: "10px 0 0" }}>
                Only an Owner can edit prompts.
              </p>
            )}
          </>
        )}
        {editing && (
          <>
            <div className="mut sm" style={{ marginBottom: 6 }}>
              Editing the prompt for <b>{scopeName}</b>.
            </div>
            <textarea
              className="input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={14}
              style={{ fontFamily: "var(--mono)", fontSize: 12, lineHeight: 1.6, width: "100%" }}
            />
            <div className="banner info" style={{ marginTop: 10 }}>
              <Alert />
              <span className="sm">
                Applies to future runs within ~1 minute — nothing already scored is re-run. Keep an
                explicit <b>"Respond with JSON only"</b> instruction: the pipeline depends on
                structured output and fails closed without it. The fairness and evidence rules are
                added automatically after your wording and cannot be edited away, so you never need
                to restate them — but a prompt that argues <i>against</i> them only makes the model
                worse at both.
              </span>
            </div>
            {err && (
              <div className="banner crit" style={{ marginTop: 10 }}>
                <Alert />
                <span>{err}</span>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                className="btn primary sm"
                disabled={save.isPending || draft.trim().length < 20}
                onClick={() => save.mutate(draft.trim())}
              >
                <Check /> {save.isPending ? "Saving…" : "Save prompt"}
              </button>
              <button
                className="btn ghost sm"
                disabled={save.isPending}
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ModelPrompts(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";

  const [mode, setMode] = useState<PromptScopeType>("platform");
  const [orgId, setOrgId] = useState("");
  const [verticalKey, setVerticalKey] = useState("");

  const { verticals } = useAdminVerticals();
  const { data: orgsData } = useQuery({
    queryKey: ["orgs"],
    queryFn: () => api.get<OrgsResponse>("/orgs"),
  });
  const orgs = useMemo(
    () => [...(orgsData?.orgs ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [orgsData],
  );

  const needsOrg = mode === "org" || mode === "org_vertical";
  const needsVertical = mode === "vertical" || mode === "org_vertical";
  // A scope is only usable once its selects are filled. Until then we keep showing the platform
  // view rather than firing a request with half a scope in it and calling the answer this org's.
  const scopeReady = (!needsOrg || orgId !== "") && (!needsVertical || verticalKey !== "");
  const scope = {
    orgId: needsOrg && scopeReady ? orgId : null,
    verticalKey: needsVertical && scopeReady ? verticalKey : null,
  };

  const orgName = (id: string | null): string =>
    (id && orgs.find((o) => o.id === id)?.name) || "that organisation";
  const verticalName = (key: string | null): string =>
    (key && verticals.find((v) => v.key === key)?.name) || key || "that vertical";
  const scopeName = scope.orgId
    ? scope.verticalKey
      ? `${orgName(scope.orgId)} · ${verticalName(scope.verticalKey)}`
      : orgName(scope.orgId)
    : scope.verticalKey
      ? `the ${verticalName(scope.verticalKey)} vertical`
      : "every org and vertical";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["model-prompts", scope.orgId, scope.verticalKey],
    queryFn: () => {
      const q = new URLSearchParams();
      if (scope.orgId) q.set("orgId", scope.orgId);
      if (scope.verticalKey) q.set("verticalKey", scope.verticalKey);
      const qs = q.toString();
      return api.get<PromptsResponse>(`/admin/model-prompts${qs ? `?${qs}` : ""}`);
    },
  });

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>AI Prompts</h1>
          <div className="sub">
            The six core prompts behind question generation, live follow-ups, interview and question scoring and
            resume screening (infrastructure prompts like the resume transcriber stay fixed)
          </div>
        </div>
        <span className="pill n">applies within ~1 min</span>
      </div>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div className="seg">
            {SCOPE_MODES.map((s) => (
              <button
                key={s.mode}
                className={mode === s.mode ? "on" : ""}
                onClick={() => setMode(s.mode)}
              >
                {s.label}
              </button>
            ))}
          </div>
          {needsVertical && (
            <select
              className="input"
              style={{ width: "auto" }}
              value={verticalKey}
              onChange={(e) => setVerticalKey(e.target.value)}
            >
              <option value="">Choose a vertical…</option>
              {verticals.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.name}
                </option>
              ))}
            </select>
          )}
          {needsOrg && (
            <select
              className="input"
              style={{ width: "auto" }}
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
            >
              <option value="">Choose an organisation…</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <p className="mut sm" style={{ margin: "10px 0 0" }}>
          {scopeReady ? (
            <>
              Showing what runs for <b>{scopeName}</b>. The most specific override wins:{" "}
              {PROMPT_SCOPE_LABEL.org_vertical.toLowerCase()} beats{" "}
              {PROMPT_SCOPE_LABEL.org.toLowerCase()}, which beats{" "}
              {PROMPT_SCOPE_LABEL.vertical.toLowerCase()}, which beats{" "}
              {PROMPT_SCOPE_LABEL.platform.toLowerCase()}.
            </>
          ) : (
            <>
              Pick{" "}
              {needsOrg && needsVertical
                ? "an organisation and a vertical"
                : needsOrg
                  ? "an organisation"
                  : "a vertical"}{" "}
              to scope these prompts. Showing the platform view meanwhile.
            </>
          )}
        </p>
      </div>

      {isLoading && <div className="card pad mut sm">Loading prompts…</div>}
      {isError && (
        <div className="banner crit">
          <span>Couldn't load the prompt registry. Please retry.</span>
        </div>
      )}
      {data?.prompts.map((p) => (
        <PromptCard
          key={p.key}
          p={p}
          isOwner={isOwner}
          scope={scope}
          scopeName={scopeName}
          orgName={orgName}
          verticalName={verticalName}
        />
      ))}

      <div className="disclaimer">
        Every save is audited, with the scope it applied to. Defaults live in code (one shared
        registry used by both the API and the scoring worker), so "Reset to default" always returns
        that scope to exactly what it would otherwise inherit.
      </div>
    </section>
  );
}
